import { useEffect, useState, useContext, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  CustomTag,
  CustomModal,
  CustomList,
  CustomCardList,
  CustomSelect,
} from "../../../../../ui";
import AuthContext from "../../../../../contexts/AuthContext";
import { PlusIcon } from "@phosphor-icons/react";
import { CircularProgress } from "@mui/material";
import { Tab, Tabs } from "@mui/material";

// ============================================================================
// HOOKS
// ============================================================================

const useSites = (contact, token) => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAllSites = useCallback(async () => {
    if (!contact?.reference || !token) return;

    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const API_HOST = process.env.REACT_APP_UPA_API_HOST;

      // Charger Webflow ET Plesk en parallèle
      const [webflowRes, pleskRes] = await Promise.all([
        axios
          .get(`${API_HOST}webflow/${contact.reference}`, { headers })
          .catch(() => ({ data: { data: [] } })),
        axios
          .get(`${API_HOST}plesk/${contact.reference}`, { headers })
          .catch(() => ({ data: { data: [] } })),
      ]);

      // Normaliser les données Webflow
      const webflowSites = (webflowRes.data.data || []).map((site) => ({
        id: `webflow-${site.webflowId}`,
        type: "webflow",
        displayName: site.displayName,
        previewUrl: site.previewUrl,
        createdOn: site.createdOn,
        rawData: site,
      }));

      const pleskSites = (pleskRes.data.data || []).map((site) => ({
        id: `plesk-${site.dataValues.pleskIdTool}`,
        type: "plesk",
        displayName: site.dataValues.displayName,
        previewUrl: site.screenshotUrl,
        createdOn: site.dataValues.createdAt,
        rawData: site.dataValues,
      }));

      const allSites = [...webflowSites, ...pleskSites].sort(
        (a, b) => new Date(b.createdOn) - new Date(a.createdOn),
      );

      setSites(allSites);
    } catch (error) {
      console.error("Erreur chargement sites:", error);
    } finally {
      setLoading(false);
    }
  }, [contact?.reference, token]);

  useEffect(() => {
    loadAllSites();
  }, [loadAllSites]);

  return { sites, loading, reload: loadAllSites };
};

// ============================================================================
// SOUS-COMPOSANTS
// ============================================================================

const AddSiteModal = ({ open, onClose, contact, token, onAdded }) => {
  const [tabIndex, setTabIndex] = useState(0); // 0=Webflow, 1=Plesk
  const [webflowToken, setWebflowToken] = useState("");
  const [selectedPleskId, setSelectedPleskId] = useState("");
  const [pleskDomains, setPleskDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);

  const API_HOST = process.env.REACT_APP_UPA_API_HOST;

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  useEffect(() => {
    const loadPleskDomains = async () => {
      setLoadingDomains(true);
      try {
        const res = await axios.get(`${API_HOST}plesk/domains`, { headers });
        setPleskDomains(res.data.domains || []);
      } catch (error) {
        console.error("Erreur chargement domaines Plesk:", error);
      } finally {
        setLoadingDomains(false);
      }
    };

    if (open && tabIndex === 1) {
      loadPleskDomains();
    }
  }, [open, tabIndex, API_HOST, token, headers]);

  const handleAdd = async () => {
    setLoading(true);
    try {
      if (tabIndex === 0) {
        // Ajouter Webflow
        await axios.post(
          `${API_HOST}webflow/${contact.reference}/add`,
          { webflowToken },
          { headers },
        );
      } else {
        // Ajouter Plesk
        await axios.post(
          `${API_HOST}plesk/${contact.reference}/add`,
          { value: selectedPleskId },
          { headers },
        );
      }

      // Reset et fermer
      setWebflowToken("");
      setSelectedPleskId("");
      onAdded();
      onClose();
    } catch (error) {
      console.error("Erreur ajout site:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setWebflowToken("");
    setSelectedPleskId("");
    setTabIndex(0);
    onClose();
  };

  const isValid =
    tabIndex === 0 ? webflowToken.trim() !== "" : selectedPleskId !== "";

  return (
    <CustomModal show={open} setShow={handleClose} title="Ajouter un site">
      <div className="c-modal-content">
        <Tabs value={tabIndex} onChange={(e, val) => setTabIndex(val)}>
          <Tab label="Webflow" />
          <Tab label="WordPress (Plesk)" />
        </Tabs>

        <div style={{ marginTop: "20px" }}>
          {tabIndex === 0 ? (
            <>
              <label className="token">
                Token Webflow <span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                placeholder="Entrez le token Webflow"
                value={webflowToken}
                onChange={(e) => setWebflowToken(e.target.value)}
                className="classic-input"
              />
            </>
          ) : (
            <>
              <label className="token">
                Domaine WordPress <span className="required-asterisk">*</span>
              </label>
              {loadingDomains ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <CircularProgress size={24} />
                </div>
              ) : (
                <CustomSelect
                  options={pleskDomains}
                  onChange={(selected) => setSelectedPleskId(selected.value)}
                />
              )}
            </>
          )}
        </div>

        <div className="modal-buttons">
          <button
            onClick={handleClose}
            className="button-large cancel-button"
            disabled={loading}
          >
            Annuler
          </button>
          <button
            onClick={handleAdd}
            className="button-large green-button validate"
            disabled={loading || !isValid}
          >
            {loading ? (
              <CircularProgress size={16} className="modal-progress" />
            ) : (
              <PlusIcon size={16} />
            )}
            <span>{loading ? "Ajout..." : "Ajouter le site"}</span>
          </button>
        </div>
      </div>
    </CustomModal>
  );
};

const WebflowSiteDetails = ({ site, contact, token, onBack, onDeleted }) => {
  const [siteInfo, setSiteInfo] = useState({});
  const [domains, setDomains] = useState([]);
  const [formResponses, setFormResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [currentResponse, setCurrentResponse] = useState({});
  const [formTab, setFormTab] = useState(0);

  const API_HOST = process.env.REACT_APP_UPA_API_HOST;

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const webflowId = site.rawData.webflowId;

  const formatDate = useCallback((dateString, includeTime = false) => {
    if (!dateString) return "Non renseigné";

    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };

    if (includeTime) {
      options.hour = "2-digit";
      options.minute = "2-digit";
    }

    return new Date(dateString).toLocaleDateString("fr-FR", options);
  }, []);

  const loadSiteInfo = useCallback(async () => {
    if (!contact?.reference || !webflowId) return;

    setLoading(true);
    try {
      const [infoResponse, domainsResponse, formResponsesResponse] =
        await Promise.all([
          axios.get(
            `${API_HOST}webflow/${contact.reference}/${webflowId}/info`,
            { headers },
          ),
          axios.get(
            `${API_HOST}webflow/${contact.reference}/${webflowId}/domains`,
            { headers },
          ),
          axios.get(
            `${API_HOST}webflow/${contact.reference}/${webflowId}/formresponses`,
            { headers },
          ),
        ]);

      setSiteInfo(infoResponse.data.data);
      setDomains(domainsResponse.data.data);
      setFormResponses(formResponsesResponse.data.data);
    } catch (error) {
      console.error("Erreur chargement détails Webflow:", error);
    } finally {
      setLoading(false);
    }
  }, [contact?.reference, webflowId, API_HOST, headers]);

  useEffect(() => {
    loadSiteInfo();
  }, [loadSiteInfo]);

  const syncSiteInfo = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(
        `${API_HOST}webflow/${contact.reference}/${webflowId}/sync`,
        {},
        { headers },
      );

      if (response.data.success) {
        await loadSiteInfo();
      }
    } catch (error) {
      console.error("Erreur synchronisation:", error);
    } finally {
      setSyncing(false);
    }
  };

  const deleteSite = async () => {
    setIsDeleting(true);
    try {
      const response = await axios.delete(
        `${API_HOST}webflow/${contact.reference}/${site.rawData.id}`,
        { headers },
      );

      if (response.data.success) {
        setIsDeleteModalOpen(false);
        onDeleted();
      }
    } catch (error) {
      console.error("Erreur suppression:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const organizedForms = useMemo(() => {
    if (!formResponses || !Array.isArray(formResponses)) {
      return {};
    }

    const organized = {};

    formResponses.forEach((response) => {
      const year = new Date(response.dateSubmitted).getFullYear().toString();

      if (!organized[year]) {
        organized[year] = [];
      }

      organized[year].push({
        ...response,
        dateSort: new Date(response.dateSubmitted).getTime(),
      });
    });

    return organized;
  }, [formResponses]);

  const formatFormResponse = useCallback((value) => {
    const fields = Object.entries(value)
      .filter(([key, val]) => val && val.trim() !== "")
      .map(([key, val]) => `${key}: ${val}`)
      .join(" | ");

    return fields.length > 150 ? fields.substring(0, 150) + "..." : fields;
  }, []);

  const formResponsesCols = useMemo(
    () => [
      {
        Header: "Formulaire",
        accessor: "displayName",
        icon: "tag",
        Cell: ({ cell: { value } }) => (
          <CustomTag value={value} className="blue" />
        ),
      },
      {
        Header: "Date de soumission",
        accessor: "dateSubmitted",
        icon: "calendar",
        Cell: ({ cell: { value } }) => <span>{formatDate(value, true)}</span>,
      },
      {
        Header: "Réponse",
        accessor: "formResponse",
        icon: "message",
        Cell: ({ cell: { value } }) => {
          const formattedResponse = formatFormResponse(value);
          const fullResponse = Object.entries(value)
            .filter(([key, val]) => val && val.trim() !== "")
            .map(([key, val]) => `${key}: ${val}`)
            .join(" | ");

          return (
            <span title={fullResponse}>
              {formattedResponse || "Aucune donnée"}
            </span>
          );
        },
      },
    ],
    [formatDate, formatFormResponse],
  );

  if (loading || syncing) {
    return (
      <div className="loader-container absolute">
        <CircularProgress size={30} />
      </div>
    );
  }

  return (
    <>
      <div className="infos-container-wrapper">
        <div className="infos-content-wrapper">
          <div className="infos-title-wrapper globe justify-between">
            <p>Webflow</p>
            <div className="site-actions-buttons">
              <button
                className="back-button"
                onClick={onBack}
                title="Retour"
              ></button>
              <button
                className="sync-button"
                onClick={syncSiteInfo}
                title="Synchroniser"
              ></button>
              {siteInfo.shortName !== null && (
                <a
                  className="button-large"
                  href={`https://${siteInfo.shortName}.design.webflow.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ouvrir dans Webflow
                </a>
              )}
              <button
                className="button-large red-button"
                onClick={() => setIsDeleteModalOpen(true)}
              >
                Supprimer le token
              </button>
            </div>
          </div>
          <div className="infos-container">
            <span className="infos-container-title name">Nom du site :</span>
            {siteInfo.displayName !== "" ? (
              <CustomTag className="" value={siteInfo.displayName} />
            ) : (
              <CustomTag className="deactivated" value="Non renseigné" />
            )}
          </div>
          <div className="infos-container">
            <span className="infos-container-title calendar">
              Date de création :
            </span>
            {siteInfo.createdOn !== null ? (
              <CustomTag className="" value={formatDate(siteInfo.createdOn)} />
            ) : (
              <CustomTag className="deactivated" value="Non renseigné" />
            )}
          </div>
          <div className="infos-container">
            <span className="infos-container-title calendar">
              Date de dernière mise à jour :
            </span>
            {siteInfo.lastPublished !== null ? (
              <CustomTag
                className=""
                value={formatDate(siteInfo.lastPublished)}
              />
            ) : (
              <CustomTag className="deactivated" value="Non renseigné" />
            )}
          </div>
        </div>
        <div className="infos-content-wrapper">
          <div className="infos-title-wrapper">
            <p>Domaines associés</p>
          </div>
          <div className="infos-container">
            <span className="infos-container-title cms">
              Domaines Webflow :
            </span>
            {domains.length > 0 ? (
              domains.map((domain, index) => (
                <CustomTag
                  key={index}
                  className=""
                  value={domain.domain || "Non renseigné"}
                />
              ))
            ) : (
              <CustomTag className="deactivated" value="Non renseigné" />
            )}
          </div>
        </div>
      </div>

      <CustomModal
        show={isDeleteModalOpen}
        setShow={setIsDeleteModalOpen}
        title="Voulez-vous vraiment supprimer le token ?"
      >
        <div className="c-modal-content">
          <div className="modal-buttons">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="button-large cancel-button"
              disabled={isDeleting}
            >
              Annuler
            </button>
            <button
              onClick={deleteSite}
              className="button-large green-button validate"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <CircularProgress size={16} className="modal-progress" />
              ) : (
                <PlusIcon size={16} />
              )}
              <span>
                {isDeleting ? "Suppression..." : "Supprimer le token"}
              </span>
            </button>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        show={isFormModalOpen}
        setShow={setIsFormModalOpen}
        title="Détails de la réponse"
      >
        {currentResponse &&
          Object.entries(currentResponse)
            .filter(([key, value]) => {
              return (
                value &&
                value !== "" &&
                value !== "false" &&
                !["id", "formId", "siteId"].includes(key)
              );
            })
            .map(([key, value]) => (
              <div key={key} className="custom__modal-group">
                <label className="tag-simple">
                  {key.replace(/([A-Z])/g, " $1").trim()} :
                </label>
                {key.toLowerCase().includes("email") ? (
                  <Link
                    to={`mailto:${value}`}
                    className="classic-input"
                    target="_self"
                    reloadDocument
                  >
                    {value}
                  </Link>
                ) : key.toLowerCase().includes("téléphone") ||
                  key.toLowerCase().includes("phone") ? (
                  <Link
                    to={`tel:${value}`}
                    className="classic-input"
                    target="_self"
                    reloadDocument
                  >
                    {value}
                  </Link>
                ) : typeof value === "string" && value.length > 100 ? (
                  <textarea
                    readOnly
                    value={value}
                    className="classic-input--textarea"
                    rows={Math.min(6, Math.ceil(value.length / 80))}
                  />
                ) : (
                  <span className="classic-input">{String(value)}</span>
                )}
              </div>
            ))}
      </CustomModal>

      <div className="tab-container form">
        {Object.keys(organizedForms).length > 0 ? (
          <div className="tabs-container">
            <div className="tabs-wrapper">
              <Tabs
                value={formTab}
                onChange={(event, value) => setFormTab(value)}
              >
                {Object.keys(organizedForms)
                  .reverse()
                  .map((year, index) => (
                    <Tab
                      value={index}
                      key={index}
                      label={`${year} (${organizedForms[year].length})`}
                    />
                  ))}
              </Tabs>
            </div>
            {Object.values(organizedForms)
              .reverse()
              .map((yearForms, index) => (
                <div key={index} role="tabpanel" hidden={formTab !== index}>
                  <CustomList
                    entity={yearForms}
                    columns={formResponsesCols}
                    isSelectable={false}
                    isFetchable={false}
                    isClickable={false}
                    isDownloadable
                    limit={20}
                    initialSortBy={[{ id: "dateSubmitted", desc: true }]}
                    onRowCLick={(rowData) => {
                      setIsFormModalOpen(true);
                      setCurrentResponse(rowData.formResponse);
                    }}
                  />
                </div>
              ))}
          </div>
        ) : (
          <CustomTag
            value="Ce client n'a aucune réponse de formulaire pour le moment."
            className="blue"
          />
        )}
      </div>
    </>
  );
};

const PleskSiteDetails = ({ site, contact, token, onBack, onDeleted }) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletePending, setIsDeletePending] = useState(false);

  const [infos, setInfos] = useState(null);

  const handleDelete = async () => {
    setIsDeletePending(true);
    await axios.delete(
      `${API_HOST}plesk/${contact.reference}/${site.rawData.pleskIdTool}`,
      { headers },
    );
    setIsDeletePending(false);
    onDeleted();
  };

  const API_HOST = process.env.REACT_APP_UPA_API_HOST;

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const [domains] = await Promise.all([
          axios.get(
            `${API_HOST}plesk/${contact.reference}/${site.rawData.id}/domains`,
            { headers },
          ),
        ]);

        setInfos({
          domains: domains.data.data,
        });
      } catch (e) {
        console.error(e.message);
      }
    };

    fetchInfo();
  }, [API_HOST, contact, site, headers]);

  const formatDate = useCallback((dateString, includeTime = false) => {
    if (!dateString) return "Non renseigné";

    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };

    if (includeTime) {
      options.hour = "2-digit";
      options.minute = "2-digit";
    }

    return new Date(dateString).toLocaleDateString("fr-FR", options);
  }, []);

  const syncSiteInfo = async () => {
    await axios.post(
      `${API_HOST}plesk/${contact.reference}/${site.rawData.pleskIdTool}/sync`,
      {},
      { headers },
    );
  };

  return (
    <>
      <div className="infos-container-wrapper">
        <div className="infos-content-wrapper">
          <div className="infos-title-wrapper globe justify-between">
            <p>Site Wordpress sur Plesk</p>
            <div className="site-actions-buttons">
              <button
                className="back-button"
                onClick={onBack}
                title="Retour"
              ></button>
              <button
                className="sync-button"
                onClick={syncSiteInfo}
                title="Synchroniser"
              ></button>
              {infos !== null && (
                <a
                  className="button-large"
                  href={`https://ns3179618.ip-51-210-208.eu/smb/web/overview/id/${site.rawData.pleskIdIntern}/type/domain`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ouvrir dans plesk
                </a>
              )}
              <button
                className="button-large red-button"
                onClick={() => setIsDeleteModalOpen(true)}
              >
                Supprimer le site
              </button>
            </div>
          </div>
          <div className="infos-container">
            <span className="infos-container-title name">Nom du site :</span>
            {site.displayName !== "" ? (
              <CustomTag className="" value={site.displayName} />
            ) : (
              <CustomTag className="deactivated" value="Non renseigné" />
            )}
          </div>
          <div className="infos-container">
            <span className="infos-container-title calendar">
              Date de création :
            </span>
            {site.createdOn !== null ? (
              <CustomTag className="" value={formatDate(site.createdOn)} />
            ) : (
              <CustomTag className="deactivated" value="Non renseigné" />
            )}
          </div>
          <div className="infos-container">
            <span className="infos-container-title name">Version de PHP :</span>
            {site.rawData !== null ? (
              <CustomTag className="" value={site.rawData.phpVersion} />
            ) : (
              <CustomTag className="deactivated" value="Non renseigné" />
            )}
          </div>
          <div className="infos-container">
            <span className="infos-container-title name">
              Version de WordPress :
            </span>
            {site.rawData !== null ? (
              <CustomTag className="" value={site.rawData.wordpressVersion} />
            ) : (
              <CustomTag className="deactivated" value="Non renseigné" />
            )}
          </div>
          <div className="infos-container">
            <span className="infos-container-title name">
              Nombre de plugins installés :
            </span>
            {site.rawData !== null ? (
              <CustomTag className="" value={site.rawData.pluginsCount} />
            ) : (
              <CustomTag className="deactivated" value="Non renseigné" />
            )}
          </div>
        </div>
        <div className="infos-content-wrapper">
          <div className="infos-title-wrapper">
            <p>Domaines associés</p>
          </div>
          <div className="infos-container">
            <span className="infos-container-title cms">
              Domaines liés à Plesk :
            </span>
            {infos !== null && infos.domains.length > 0 ? (
              infos.domains.map((domain, index) => (
                <CustomTag
                  key={index}
                  className=""
                  value={domain || "Non renseigné"}
                />
              ))
            ) : (
              <CustomTag className="deactivated" value="Non renseigné" />
            )}
          </div>
        </div>
      </div>
      <CustomModal
        show={isDeleteModalOpen}
        setShow={setIsDeleteModalOpen}
        title="Voulez-vous vraiment supprimer le site ?"
      >
        <div className="c-modal-content">
          <div className="modal-buttons">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="button-large cancel-button"
              disabled={isDeletePending}
            >
              Annuler
            </button>
            <button
              onClick={handleDelete}
              className="button-large green-button validate"
              disabled={isDeletePending}
            >
              {isDeletePending && (
                <CircularProgress size={16} className="modal-progress" />
              )}
              <span>
                {isDeletePending ? "Suppression..." : "Supprimer le site"}
              </span>
            </button>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const CustomerSite = ({ contact, onSitesChange }) => {
  const { token } = useContext(AuthContext);
  const { sites, loading, reload } = useSites(contact, token);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const handleSiteDeleted = () => {
    setSelectedSite(null);
    reload();
    if (onSitesChange) onSitesChange();
  };

  const handleSiteAdded = () => {
    reload();
    if (onSitesChange) onSitesChange();
  };

  if (loading) {
    return (
      <div className="loader-container absolute">
        <CircularProgress size={30} />
      </div>
    );
  }

  if (!selectedSite) {
    return (
      <>
        <CustomCardList
          data={sites}
          titleAccessor="displayName"
          imageAccessor="previewUrl"
          onAddClick={() => setIsAddModalOpen(true)}
          onCardClick={setSelectedSite}
          renderBadge={(site) => (
            <CustomTag
              value={site.type === "webflow" ? "Webflow" : "WordPress"}
              className={site.type === "webflow" ? "blue" : "green"}
            />
          )}
        />

        <AddSiteModal
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          contact={contact}
          token={token}
          onAdded={handleSiteAdded}
        />
      </>
    );
  }

  return (
    <>
      {selectedSite.type === "webflow" ? (
        <WebflowSiteDetails
          site={selectedSite}
          contact={contact}
          token={token}
          onBack={() => setSelectedSite(null)}
          onDeleted={handleSiteDeleted}
        />
      ) : (
        <PleskSiteDetails
          site={selectedSite}
          contact={contact}
          token={token}
          onBack={() => setSelectedSite(null)}
          onDeleted={handleSiteDeleted}
        />
      )}
    </>
  );
};

export default CustomerSite;
